/**
 * Single source of truth for verified production AI model rates, context limits,
 * capabilities, and official documentation citations.
 *
 * Requirements:
 * - Only currently available/supported production models.
 * - Zero hardcoded/mock/demo catalog entries.
 * - Strict provider pricing per 1,000,000 tokens (USD).
 * - Verified context and output generation limits.
 * - Never invent values: null indicates unavailable/unsupported.
 */
export const VERIFIED_MODEL_CATALOG = {
    // ============================================================
    // OpenAI Official Production Models
    // Source: https://openai.com/api/pricing/
    // ============================================================
    'gpt-4o': {
        modelId: 'gpt-4o',
        name: 'GPT-4o (Omni)',
        providerType: 'openai',
        inputCostPer1M: 2.5,
        outputCostPer1M: 10.0,
        contextWindow: 128000,
        maxOutputTokens: 16384,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'OpenAI Official API Pricing (https://openai.com/api/pricing/)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'gpt-4o-mini': {
        modelId: 'gpt-4o-mini',
        name: 'GPT-4o Mini',
        providerType: 'openai',
        inputCostPer1M: 0.15,
        outputCostPer1M: 0.6,
        contextWindow: 128000,
        maxOutputTokens: 16384,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'OpenAI Official API Pricing (https://openai.com/api/pricing/)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    o1: {
        modelId: 'o1',
        name: 'OpenAI o1',
        providerType: 'openai',
        inputCostPer1M: 15.0,
        outputCostPer1M: 60.0,
        contextWindow: 200000,
        maxOutputTokens: 100000,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'OpenAI Official API Pricing (https://openai.com/api/pricing/)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'o3-mini': {
        modelId: 'o3-mini',
        name: 'OpenAI o3-mini',
        providerType: 'openai',
        inputCostPer1M: 1.1,
        outputCostPer1M: 4.4,
        contextWindow: 200000,
        maxOutputTokens: 100000,
        supportsVision: false,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'OpenAI Official API Pricing (https://openai.com/api/pricing/)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'text-embedding-3-small': {
        modelId: 'text-embedding-3-small',
        name: 'Text Embedding 3 Small',
        providerType: 'openai',
        inputCostPer1M: 0.02,
        outputCostPer1M: null,
        contextWindow: 8191,
        maxOutputTokens: null,
        supportsVision: false,
        supportsStreaming: false,
        supportsFunctionCalling: false,
        supportsEmbeddings: true,
        source: 'OpenAI Official API Pricing (https://openai.com/api/pricing/)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'text-embedding-3-large': {
        modelId: 'text-embedding-3-large',
        name: 'Text Embedding 3 Large',
        providerType: 'openai',
        inputCostPer1M: 0.13,
        outputCostPer1M: null,
        contextWindow: 8191,
        maxOutputTokens: null,
        supportsVision: false,
        supportsStreaming: false,
        supportsFunctionCalling: false,
        supportsEmbeddings: true,
        source: 'OpenAI Official API Pricing (https://openai.com/api/pricing/)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    // ============================================================
    // Google Gemini Official Production Models
    // Source: https://ai.google.dev/pricing
    // ============================================================
    'gemini-1.5-flash': {
        modelId: 'gemini-1.5-flash',
        name: 'Gemini 1.5 Flash',
        providerType: 'gemini',
        inputCostPer1M: 0.075,
        outputCostPer1M: 0.3,
        contextWindow: 1048576,
        maxOutputTokens: 8192,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Google AI Official API Pricing (https://ai.google.dev/pricing)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'gemini-1.5-pro': {
        modelId: 'gemini-1.5-pro',
        name: 'Gemini 1.5 Pro',
        providerType: 'gemini',
        inputCostPer1M: 1.25,
        outputCostPer1M: 5.0,
        contextWindow: 2097152,
        maxOutputTokens: 8192,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Google AI Official API Pricing (https://ai.google.dev/pricing)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'text-embedding-004': {
        modelId: 'text-embedding-004',
        name: 'Text Embedding 004',
        providerType: 'gemini',
        inputCostPer1M: 0.025,
        outputCostPer1M: null,
        contextWindow: 2048,
        maxOutputTokens: null,
        supportsVision: false,
        supportsStreaming: false,
        supportsFunctionCalling: false,
        supportsEmbeddings: true,
        source: 'Google AI Official API Pricing (https://ai.google.dev/pricing)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    // ============================================================
    // Anthropic Claude Official Production Models
    // Source: https://www.anthropic.com/pricing
    // ============================================================
    'claude-3-5-sonnet-20241022': {
        modelId: 'claude-3-5-sonnet-20241022',
        name: 'Claude 3.5 Sonnet',
        providerType: 'anthropic',
        inputCostPer1M: 3.0,
        outputCostPer1M: 15.0,
        contextWindow: 200000,
        maxOutputTokens: 8192,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Anthropic Official API Pricing (https://www.anthropic.com/pricing)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'claude-3-5-sonnet': {
        modelId: 'claude-3-5-sonnet',
        name: 'Claude 3.5 Sonnet (Latest Alias)',
        providerType: 'anthropic',
        inputCostPer1M: 3.0,
        outputCostPer1M: 15.0,
        contextWindow: 200000,
        maxOutputTokens: 8192,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Anthropic Official API Pricing (https://www.anthropic.com/pricing)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'claude-3-5-haiku-20241022': {
        modelId: 'claude-3-5-haiku-20241022',
        name: 'Claude 3.5 Haiku',
        providerType: 'anthropic',
        inputCostPer1M: 0.8,
        outputCostPer1M: 4.0,
        contextWindow: 200000,
        maxOutputTokens: 8192,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Anthropic Official API Pricing (https://www.anthropic.com/pricing)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'claude-3-5-haiku': {
        modelId: 'claude-3-5-haiku',
        name: 'Claude 3.5 Haiku (Latest Alias)',
        providerType: 'anthropic',
        inputCostPer1M: 0.8,
        outputCostPer1M: 4.0,
        contextWindow: 200000,
        maxOutputTokens: 8192,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Anthropic Official API Pricing (https://www.anthropic.com/pricing)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'claude-3-opus-20240229': {
        modelId: 'claude-3-opus-20240229',
        name: 'Claude 3 Opus',
        providerType: 'anthropic',
        inputCostPer1M: 15.0,
        outputCostPer1M: 75.0,
        contextWindow: 200000,
        maxOutputTokens: 4096,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Anthropic Official API Pricing (https://www.anthropic.com/pricing)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'claude-3-opus': {
        modelId: 'claude-3-opus',
        name: 'Claude 3 Opus (Latest Alias)',
        providerType: 'anthropic',
        inputCostPer1M: 15.0,
        outputCostPer1M: 75.0,
        contextWindow: 200000,
        maxOutputTokens: 4096,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Anthropic Official API Pricing (https://www.anthropic.com/pricing)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'claude-3-haiku-20240307': {
        modelId: 'claude-3-haiku-20240307',
        name: 'Claude 3 Haiku',
        providerType: 'anthropic',
        inputCostPer1M: 0.25,
        outputCostPer1M: 1.25,
        contextWindow: 200000,
        maxOutputTokens: 4096,
        supportsVision: true,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Anthropic Official API Pricing (https://www.anthropic.com/pricing)',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    // ============================================================
    // Self-Hosted Open Weights Models
    // Source: Official model developer specifications
    // ============================================================
    'llama-3.1-8b': {
        modelId: 'llama-3.1-8b',
        name: 'Llama 3.1 8B (Self-Hosted)',
        providerType: 'self_hosted',
        inputCostPer1M: 0.0,
        outputCostPer1M: 0.0,
        contextWindow: 131072,
        maxOutputTokens: 4096,
        supportsVision: false,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Meta Llama Official Specs / Self-Hosted Infrastructure',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'llama-3.3-70b': {
        modelId: 'llama-3.3-70b',
        name: 'Llama 3.3 70B (Self-Hosted)',
        providerType: 'self_hosted',
        inputCostPer1M: 0.0,
        outputCostPer1M: 0.0,
        contextWindow: 131072,
        maxOutputTokens: 4096,
        supportsVision: false,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Meta Llama Official Specs / Self-Hosted Infrastructure',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'mistral-7b': {
        modelId: 'mistral-7b',
        name: 'Mistral 7B Instruct (Self-Hosted)',
        providerType: 'self_hosted',
        inputCostPer1M: 0.0,
        outputCostPer1M: 0.0,
        contextWindow: 32768,
        maxOutputTokens: 4096,
        supportsVision: false,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'Mistral AI Official Specs / Self-Hosted Infrastructure',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
    'deepseek-r1': {
        modelId: 'deepseek-r1',
        name: 'DeepSeek R1 (Self-Hosted)',
        providerType: 'self_hosted',
        inputCostPer1M: 0.0,
        outputCostPer1M: 0.0,
        contextWindow: 65536,
        maxOutputTokens: 8192,
        supportsVision: false,
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsEmbeddings: false,
        source: 'DeepSeek Official Specs / Self-Hosted Infrastructure',
        lastVerifiedDate: '2026-10-05',
        isRetired: false,
    },
};
/**
 * Retrieves verified model metadata for a given model ID.
 * Returns undefined if model is not recognized in official verified specifications.
 */
export function getVerifiedModelSpec(modelId) {
    return VERIFIED_MODEL_CATALOG[modelId];
}
/**
 * Dynamically builds the complete Model Pricing & Context Catalog by merging
 * real runtime Firestore provider configurations with verified official rate cards.
 *
 * Rules:
 * 1. Runtime configured models are enriched with official pricing and context metadata.
 * 2. Unverified values are never fabricated and return null ("Not available").
 * 3. Status reflects both provider-level and model-level enablement.
 */
export function buildDynamicCatalog(configuredProviders) {
    const catalogItems = [];
    const processedModelKeys = new Set();
    // 1. Process all models configured in runtime Firestore providers
    for (const provider of configuredProviders) {
        for (const model of provider.models || []) {
            const spec = getVerifiedModelSpec(model.modelId);
            const key = `${provider.id}_${model.modelId}`;
            processedModelKeys.add(key);
            // Input rate per 1M: prioritize verified official spec; if model document has custom rate, convert
            const inputCostPer1M = spec?.inputCostPer1M ??
                (model.inputCostPer1kTokens !== undefined && model.inputCostPer1kTokens !== null
                    ? Number((model.inputCostPer1kTokens * 1000).toFixed(4))
                    : null);
            const outputCostPer1M = spec?.outputCostPer1M ??
                (model.outputCostPer1kTokens !== undefined && model.outputCostPer1kTokens !== null
                    ? Number((model.outputCostPer1kTokens * 1000).toFixed(4))
                    : null);
            // Rates per 1k (for legacy Gateway cost estimation)
            const inputCostPer1k = inputCostPer1M !== null ? Number((inputCostPer1M / 1000).toFixed(6)) : null;
            const outputCostPer1k = outputCostPer1M !== null ? Number((outputCostPer1M / 1000).toFixed(6)) : null;
            // Context and generation limits
            const contextWindow = spec?.contextWindow ?? model.maxInputTokens ?? null;
            const maxOutputTokens = spec?.maxOutputTokens ?? model.maxOutputTokens ?? null;
            const isModelActive = (model.enabled ?? true) && provider.enabled;
            catalogItems.push({
                id: model.id || key,
                modelId: model.modelId,
                name: model.name || spec?.name || model.modelId,
                providerType: provider.type,
                providerId: provider.id,
                providerName: provider.name,
                providerEnabled: provider.enabled,
                modelEnabled: model.enabled ?? true,
                inputCostPer1M,
                outputCostPer1M,
                inputCostPer1k,
                outputCostPer1k,
                contextWindow,
                maxOutputTokens,
                supportsVision: spec?.supportsVision ?? false,
                supportsStreaming: spec?.supportsStreaming ?? true,
                supportsFunctionCalling: spec?.supportsFunctionCalling ?? true,
                supportsEmbeddings: spec?.supportsEmbeddings ?? false,
                source: spec?.source ?? 'Provider Runtime Document (Custom / Unverified)',
                lastVerifiedDate: spec?.lastVerifiedDate ?? 'Not verified',
                isConfiguredInRuntime: true,
                status: isModelActive ? 'available' : 'disabled',
            });
        }
    }
    // 2. Also expose verified official models that are not yet configured in runtime
    // so admins can view official catalog entries
    for (const [modelId, spec] of Object.entries(VERIFIED_MODEL_CATALOG)) {
        if (spec.isRetired)
            continue;
        const alreadyConfigured = configuredProviders.some((p) => p.type === spec.providerType && (p.models || []).some((m) => m.modelId === modelId));
        if (!alreadyConfigured) {
            const inputCostPer1M = spec.inputCostPer1M;
            const outputCostPer1M = spec.outputCostPer1M;
            catalogItems.push({
                id: `official_${spec.providerType}_${modelId}`,
                modelId: spec.modelId,
                name: spec.name,
                providerType: spec.providerType,
                providerId: undefined,
                providerName: getProviderDisplayName(spec.providerType),
                providerEnabled: false,
                modelEnabled: false,
                inputCostPer1M,
                outputCostPer1M,
                inputCostPer1k: inputCostPer1M !== null ? inputCostPer1M / 1000 : null,
                outputCostPer1k: outputCostPer1M !== null ? outputCostPer1M / 1000 : null,
                contextWindow: spec.contextWindow,
                maxOutputTokens: spec.maxOutputTokens,
                supportsVision: spec.supportsVision,
                supportsStreaming: spec.supportsStreaming,
                supportsFunctionCalling: spec.supportsFunctionCalling,
                supportsEmbeddings: spec.supportsEmbeddings,
                source: spec.source,
                lastVerifiedDate: spec.lastVerifiedDate,
                isConfiguredInRuntime: false,
                status: 'unconfigured',
            });
        }
    }
    return catalogItems;
}
function getProviderDisplayName(type) {
    switch (type) {
        case 'openai':
            return 'OpenAI';
        case 'gemini':
            return 'Google Gemini';
        case 'anthropic':
            return 'Anthropic';
        case 'self_hosted':
            return 'Self-Hosted';
        default:
            return type;
    }
}
//# sourceMappingURL=catalog.js.map